import { DatePickerField, ModeEnum } from "@/deps/ui-library";
import { usePackCalendar } from "@/app/session";

/**
 * Accounting's date field: RDS `DatePickerField`, with the calendar the organisation's pack uses.
 * A Nepal-pack company sees Bikram Sambat with the EN/NP toggle; any other pack sees AD and no
 * toggle. What goes in and comes out is always an ISO AD date (ADR-0033): BS is display only.
 *
 * `onChange` gets "" while the date is incomplete or cleared. The picker reads its calendar once
 * when it mounts, so it remounts if the pack's calendar changes.
 */
interface DateFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** The visible label. Without one, `aria-label` names the field (a filter bar). */
  label?: string;
  "aria-label"?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  /** The earliest and latest dates offered, inclusive (ISO). */
  min?: string;
  max?: string;
  /** A clear button once there is a value; for an optional date. On by default in a filter. */
  clearable?: boolean;
  /** `input` in a form; `filter` in a filter bar. */
  variant?: "input" | "filter";
  className?: string;
}

function useCalendarProps() {
  const nepali = usePackCalendar() === "bikram-sambat";
  return { key: nepali ? "bs" : "ad", isNepali: nepali, showToggle: nepali };
}

export function DateField({
  value,
  onChange,
  label,
  "aria-label": ariaLabel,
  error,
  helperText,
  required,
  disabled,
  min,
  max,
  clearable,
  variant = "input",
  className,
}: DateFieldProps) {
  const { key, ...calendar } = useCalendarProps();
  return (
    <DatePickerField
      key={key}
      {...calendar}
      mode={ModeEnum.SINGLE}
      variant={variant}
      label={label}
      aria-label={ariaLabel}
      errorText={error}
      helperText={helperText}
      required={required}
      disabled={disabled}
      disableDateBefore={min}
      disableDateAfter={max}
      clearable={clearable}
      className={className}
      value={value}
      onChange={(next) => onChange(typeof next === "string" ? next : next.startDate)}
    />
  );
}

export interface DateRange {
  from: string;
  to: string;
}

/**
 * A from–to pair as one RDS range field: the start is picked first, then the end, and an end
 * before the start cannot be picked. `onChange` gets both bounds; one is "" while it is unset.
 */
export function DateRangeField({
  value,
  onChange,
  label,
  "aria-label": ariaLabel,
  min,
  max,
  clearable,
  variant = "filter",
  className,
}: {
  value: DateRange;
  onChange: (value: DateRange) => void;
  label?: string;
  "aria-label"?: string;
  min?: string;
  max?: string;
  /** Clear buttons on the two bounds. On by default in a filter. */
  clearable?: boolean;
  variant?: "input" | "filter";
  className?: string;
}) {
  const { key, ...calendar } = useCalendarProps();
  return (
    <DatePickerField
      key={key}
      {...calendar}
      mode={ModeEnum.RANGE}
      clearable={clearable}
      variant={variant}
      label={label}
      aria-label={ariaLabel}
      disableDateBefore={min}
      disableDateAfter={max}
      className={className}
      value={{ startDate: value.from, endDate: value.to }}
      onChange={(next) =>
        typeof next === "string"
          ? onChange({ from: next, to: value.to })
          : onChange({ from: next.startDate, to: next.endDate })
      }
    />
  );
}
